// deps
import cx from "classnames";
import React, { ReactElement, memo, useCallback, useMemo } from "react";

import { NETBANKING_PAYMENT_METHOD_CODE, PAYMENT_CODES } from "@swiggy-private/payment-core";
// components
import { ArrowSvgIcon } from "@payment-ui/widgets/arrow-svg-icon";

// interfaces
import { BankData, INetBankingPaymentGroup, NetBankingPaymentGroupActions } from "@payment-ui/interfaces";

// test-ids
import { NETBANKING_MORE_BANKS_TEST_IDS } from "@swiggy-private/payment-test-ids";

import styles from "./NetbankingMoreBanks.v4.scss";

interface NetbankingMoreBanksProps {
    paymentGroupData: INetBankingPaymentGroup["paymentData"];
    paymentInProgress: boolean;
    onPay: NetBankingPaymentGroupActions["onPay"];
    netBankingList: BankData[];
}

interface BankItemProps {
    bankName: string;
    bankCode: string;
    enabled: boolean;
    onClick: (params: { bankName: string; bankCode: string }) => void;
}

export const BankItem = memo(function BankItem({
    bankCode,
    bankName,
    onClick,
    enabled,
}: BankItemProps): React.ReactElement {
    const onItemClick = useCallback(
        (event: React.MouseEvent): void => {
            event.preventDefault();
            if (!enabled) {
                return;
            }
            onClick({ bankCode, bankName });
        },
        [bankCode, bankName, enabled, onClick],
    );
    const itemClass = useMemo(
        () =>
            cx(styles.netBankingListItem, {
                [styles.netBankingListItemDisabled]: !enabled,
            }),
        [enabled],
    );

    return (
        <div className={styles.netBankingListItemContainer} onClick={onItemClick}>
            <div className={itemClass}>
                <ArrowSvgIcon className={styles.netBankingListItemIcon} />
                <span>{bankName}</span>
            </div>
        </div>
    );
});

export default function NetbankingMoreBanks({
    paymentGroupData,
    onPay,
    netBankingList,
}: NetbankingMoreBanksProps): ReactElement {
    const onBankClick = (params: { bankName: string; bankCode: string }): void => {
        onPay(
            {
                displayName: params.bankName,
                groupName: paymentGroupData.groupName,
                name: params.bankName,
                nbCode: params.bankCode,
                paymentCode: PAYMENT_CODES.NETBANKING,
                meta: {
                    code: params.bankCode,
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
            {
                bankCode: params.bankCode,
                bankName: params.bankName,
                merchantId: paymentGroupData.meta?.merchantId,
                paymentMethodType: NETBANKING_PAYMENT_METHOD_CODE,
            },
        );
    };

    return (
        <div className={styles.fixedContainer} data-testid={NETBANKING_MORE_BANKS_TEST_IDS.CONTAINER}>
            <div className={styles.netBankingList} data-testid={NETBANKING_MORE_BANKS_TEST_IDS.LIST}>
                {netBankingList.map((bankData: BankData) => {
                    const { bankCode, bankName, enabled } = bankData;

                    return (
                        <BankItem
                            key={`MORE_BANKS_${bankCode}_${bankName}`}
                            bankName={bankName}
                            bankCode={bankCode}
                            enabled={enabled}
                            onClick={onBankClick}
                        />
                    );
                })}
            </div>
        </div>
    );
}
